import type { RefObject } from "react"
import { useCallback, useMemo } from "react"
import { useZeroVirtualizer } from "@rocicorp/zero-virtual/react"

import type { Company } from "@productlane/zero"
import type { CompaniesSelection } from "@productlane/zero/queries"
import { queries } from "@productlane/zero/queries"

import { useAuthData } from "@/lib/zero"
import { useCompaniesFilterState } from "../filter/companies-filter-state"
import {
	resolveCompaniesSort,
	useCompaniesSortState,
} from "./companies-sort-state"

interface CompaniesCursor {
	name: string
	createdAt: number
	revenue: number | null
	size: number | null
	tierName: string | null
	statusName: string | null
	id: string
}

const flipOrder = (order: "asc" | "desc"): "asc" | "desc" =>
	order === "asc" ? "desc" : "asc"

const toCompaniesCursor = (row: Company): CompaniesCursor => ({
	name: row.name,
	createdAt: row.createdAt,
	revenue: row.revenue,
	size: row.size,
	tierName: row.tierName,
	statusName: row.statusName,
	id: row.id,
})

const getRowKey = (row: Company) => row.id

interface UseCompaniesVirtualizerArgs {
	scrollElementRef: RefObject<HTMLElement | null>
	estimateSize: number
	search: string
	anchorResetKey: number
}

/** The filters the list query applies, shared with select-all so both match the same rows. */
export const useCompaniesFilters = (): CompaniesSelection["filters"] => {
	const { tiers, statuses, ownerIds, revenue, size, sources, negations } =
		useCompaniesFilterState()

	return useMemo(() => {
		if (
			tiers.length === 0 &&
			statuses.length === 0 &&
			ownerIds.length === 0 &&
			revenue === null &&
			size === null &&
			sources.length === 0
		)
			return undefined
		return {
			negations,
			tiers: tiers.length > 0 ? tiers : undefined,
			statuses: statuses.length > 0 ? statuses : undefined,
			ownerIds: ownerIds.length > 0 ? ownerIds : undefined,
			revenue: revenue ?? undefined,
			size: size ?? undefined,
			sources: sources.length > 0 ? sources : undefined,
		}
	}, [tiers, statuses, ownerIds, revenue, size, sources, negations])
}

export const useCompaniesVirtualizer = ({
	scrollElementRef,
	estimateSize,
	search,
	anchorResetKey,
}: UseCompaniesVirtualizerArgs) => {
	const decodedToken = useAuthData()
	const { sortType } = useCompaniesSortState()
	const filters = useCompaniesFilters()

	const { sortColumn, sortOrder } = resolveCompaniesSort(sortType)

	const filtersHash = useMemo(() => JSON.stringify(filters), [filters])
	const trimmedSearch = search.trim()

	// Bumped before a bulk delete so zero-virtual drops backward anchors that
	// point at rows the mutation removes, which otherwise throws "Assertion failed".
	const listContextParams = useMemo(
		() => ({
			sortColumn,
			sortOrder,
			filtersHash,
			search: trimmedSearch,
			anchorResetKey,
		}),
		[sortColumn, sortOrder, filtersHash, trimmedSearch, anchorResetKey],
	)

	const getPageQuery = useCallback(
		({
			limit,
			start,
			dir,
			settled,
		}: {
			limit: number
			start: (CompaniesCursor & Partial<Company>) | null
			dir: "forward" | "backward"
			settled: boolean
		}) => ({
			query: queries.companiesWithContacts({
				filters,
				search: trimmedSearch || undefined,
				sortColumn,
				sortOrder: dir === "forward" ? sortOrder : flipOrder(sortOrder),
				limit,
				startAfter: start ? toCompaniesCursor(start as Company) : undefined,
			}),
			options: { ttl: settled ? "5m" : "10s" } as const,
		}),
		[filters, trimmedSearch, sortColumn, sortOrder],
	)

	const getSingleQuery = useCallback(
		({ id }: { id: string }) => ({ query: queries.companyById(id) }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[decodedToken],
	)

	const getScrollElement = useCallback(
		() => scrollElementRef.current,
		[scrollElementRef],
	)

	const estimateSizeFn = useCallback(() => estimateSize, [estimateSize])

	return useZeroVirtualizer({
		getScrollElement,
		estimateSize: estimateSizeFn,
		overscan: 15,
		listContextParams,
		getPageQuery,
		getSingleQuery,
		toStartRow: toCompaniesCursor,
		getRowKey,
	})
}
